import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import FeatureItem from "./FeatureItem";
import { getAuthUrl } from "@/utils/redirectUtils";

export interface PricingTierProps {
  name: string;
  monthlyPrice: number;
  annualPrice?: number;
  description: string;
  bestFor: string;
  features: React.ReactNode[];
  cta: string;
  highlighted: boolean;
  badge?: string;
  onSubscribe: (tierName: string) => void;
}

const getTierSlug = (name: string): string => {
  return name.toLowerCase();
};

const PricingTier = ({
  name,
  monthlyPrice,
  annualPrice,
  description,
  bestFor,
  features,
  cta,
  highlighted,
  badge,
  onSubscribe
}: PricingTierProps) => {
  const isFree = monthlyPrice === 0;
  const tierSlug = getTierSlug(name);

  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
      className={`rounded-lg border bg-card text-card-foreground shadow-sm ${
        highlighted ? 'border-primary ring-2 ring-primary/20 shadow-md' : ''
      }`}
    >
      <div className="flex flex-col h-full">
        <div className="p-6 space-y-4">
          {(badge || highlighted) && (
            <Badge className="mb-2 bg-primary/10 text-primary border-0">
              {badge ?? "Most Popular"}
            </Badge>
          )}
          <h3 className="text-2xl font-bold text-foreground">{name}</h3>
          <div className="space-y-1">
            <div className="flex items-baseline text-foreground">
              {isFree ? (
                <span className="text-3xl font-bold">Free</span>
              ) : (
                <>
                  <span className="text-3xl font-bold">${monthlyPrice}</span>
                  <span className="text-muted-foreground ml-1">/mo</span>
                </>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              {annualPrice ? (
                <>or ${annualPrice}/yr — save ~17%</>
              ) : (
                <>Billed monthly · 7-day free trial</>
              )}
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {description}
          </p>
          <div className="pt-2">
            <Badge variant="secondary" className="text-xs">
              Best for: {bestFor}
            </Badge>
          </div>
        </div>
        <div className="p-6 space-y-4 border-t mt-auto bg-background">
          <ul className="space-y-3">
            {features.map((feature, index) => (
              <FeatureItem key={index} feature={feature} />
            ))}
          </ul>
          <Button 
            className="w-full group"
            variant={highlighted ? "default" : "outline"}
            onClick={() => {
              window.location.href = getAuthUrl();
            }}
          >
            {cta}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
          <div className="text-center">
            <Link 
              to={`/plans/${tierSlug}`}
              className="text-xs text-primary hover:underline"
            >
              Learn more about {name} →
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PricingTier;